import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { Layout, Typography, Table, Col } from "antd";
import axios from "api/axios";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { transformDpatTableRows, rowHasAllEvidenceLinks } from "utils/renderDpatEvidenceLink";
import { getAttributeValue, getFileLinkIfExist, getYearFromDateValue, isDateInSelectedYear } from "utils/utils";

const { Title } = Typography;

function isEstablishedBySelectedYear(establishmentDate, selectedYear) {
  const yearNum = Number(selectedYear);
  if (!Number.isFinite(yearNum)) {
    return false;
  }

  const establishedYear = getYearFromDateValue(establishmentDate);
  return establishedYear != null && establishedYear <= yearNum;
}

function isReportInSelectedYear(reportDate, selectedYear) {
  return isDateInSelectedYear(reportDate, selectedYear);
}

const ClientServiceFunctionality = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [clientService, setClientService] = useState([]);
  const [fulfillment, setFulfillment] = useState("Not Fulfilled");

  const clienServiceColumn = [
    { title: "Date of Establishment", dataIndex: "date", key: "date" },
    { title: "Office Available (Yes/No)", dataIndex: "officeAvailability", key: "officeAvailability" },
    { title: "Complaint’s Book available (Yes/No)", dataIndex: "bookAvailability", key: "bookAvailability" },
    { title: "Work plan available (Yes/No) ", dataIndex: "planAvailability", key: "planAvailability" },
    { title: "Schedule Officer Name", dataIndex: "officerName", key: "officerName" },
    { title: "Dedicated telephone number", dataIndex: "phone", key: "phone" }
  ];

  const clienServiceReportColumn = [
    { title: "Title of Report on activities", dataIndex: "report", key: "report" },
    { title: "Date of Report", dataIndex: "date", key: "date" },
    { title: "Attachments", dataIndex: "attachments", key: "attachments" }
  ];

  const displayClientService = useMemo(() => {
    const rows = clientService?.data || [];
    const reports = clientService?.report || [];
    const filteredRows = rows.filter((row) => isEstablishedBySelectedYear(row?.date, year));
    const filteredReports = reports.filter((row) => isReportInSelectedYear(row?.date, year));

    if (!clientService || typeof clientService !== "object") {
      return { data: filteredRows, report: filteredReports, fulfillment: "Not Fulfilled" };
    }

    return {
      ...clientService,
      data: filteredRows,
      report: filteredReports,
    };
  }, [clientService, year]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI4",
      area: "Transparency, Accountability & Participation",
      clientService: displayClientService,
    }),
  }));

  const DPAT_SLUG = "c4.0-4.1";

  const SERVICE_LINK_KEYS = ["bookAvailability", "planAvailability"];
  const REPORT_LINK_KEYS = ["attachments"];

  const setClientServiceDataDisplay = (data, reports, selectedYear) => {
    const clientServiceTemp = [];
    const clientServiceReport = [];

    let nextFulfillment = "Not Fulfilled";

    if (data?.length > 0) {
      data.forEach((val, idx) => {
        const establishedDate = getAttributeValue("Established Date", val);
        if (!isEstablishedBySelectedYear(establishedDate, selectedYear)) {
          return;
        }

        const complaintLink = getFileLinkIfExist(reports, "fHGCEnkTRnW", val.trackedEntity);
        const workPlanLink = getFileLinkIfExist(reports, "RnjmtOxz2V5", val.trackedEntity);
        const reportLink = getFileLinkIfExist(reports, "TH0o7vTWcAy", val.trackedEntity);

        clientServiceTemp.push({
          key: idx,
          date: establishedDate,
          officeAvailability: getAttributeValue("Office Available (Yes/No)", val) === "true" ? "YES" : "NO",
          officerName: getAttributeValue("Schedule Officer Name", val),
          phone: getAttributeValue("Dedicated Mobile Number", val),
          bookAvailability: complaintLink ? (
            <a
              className="px-2 text-primary fw-bold text-decoration-underline"
              href={`https://dddp.gov.gh/api/events/files?eventUid=${complaintLink}&dataElementUid=fHGCEnkTRnW`} target="_blank"
              rel="noopener noreferrer"
              title="Click here to see the uploaded document"
            >
              View Commplaint Book
            </a>
          ) : (
            "Not Uploaded"
          ),

          planAvailability: workPlanLink ? (
            <a
              className="px-2 text-primary fw-bold text-decoration-underline"
              href={`https://dddp.gov.gh/api/events/files?eventUid=${workPlanLink}&dataElementUid=RnjmtOxz2V5`} target="_blank"
              rel="noopener noreferrer"
              title="Click here to see the uploaded document"
            >
              View Work Plan
            </a>
          ) : (
            "Not Uploaded"
          )
        });

        const currentReport = reports.find(rep => rep.trackedEntity === val.trackedEntity);

        if (currentReport) {
          const dataReportSet = {
            report: "",
            date: "",
            attachments: reportLink ? (
              <a
                className="px-2 text-primary fw-bold text-decoration-underline"
                href={`https://dddp.gov.gh/api/events/files?eventUid=${reportLink}&dataElementUid=TH0o7vTWcAy`} target="_blank"
                rel="noopener noreferrer"
                title="Click here to see the uploaded document"
              >
                View Report
              </a>
            ) : (
              "Not Uploaded"
            )
          };

          currentReport.dataValues.forEach(rep => {
            if (rep.dataElement === "EXpYmoD23TM") {
              dataReportSet.report = rep.value;
            } else if (rep.dataElement === "ISuGmawTpiF") {
              dataReportSet.date = rep.value;
            }
          });

          if (!isReportInSelectedYear(dataReportSet.date, selectedYear)) {
            return;
          }

          clientServiceReport.push(dataReportSet);
        }
      });
    }

    if (clientServiceTemp.length > 0 && clientServiceReport.length > 0) {
      const serviceLinksComplete = clientServiceTemp.every((row) =>
        rowHasAllEvidenceLinks(row, ["bookAvailability", "planAvailability"]),
      );
      const reportLinksComplete = clientServiceReport.every((row) =>
        rowHasAllEvidenceLinks(row, ["attachments"]),
      );

      if (serviceLinksComplete && reportLinksComplete) {
        nextFulfillment = "Fulfilled";
      }
    }

    setFulfillment(nextFulfillment);
    setClientService({ data: clientServiceTemp, fulfillment: nextFulfillment, report: clientServiceReport });
  };

  const getClientServiceData = useCallback(() => {
    if (!districtId) {
      setClientService({ data: [], fulfillment: "Not Fulfilled", report: [] });
      setFulfillment("Not Fulfilled");
      return;
    }

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=GciA0HJcRzN`)
      .then(result => {
        const instances = result.data?.instances || [];
        if (instances.length === 0) {
          setClientService({ data: [], fulfillment: "Not Fulfilled", report: [] });
          setFulfillment("Not Fulfilled");
          return;
        }

        axios
          .get(`/tracker/events?program=GciA0HJcRzN&orgUnit=${districtId}`)
          .then(resp => {
            setClientServiceDataDisplay(instances, resp.data?.instances || [], year);
          })
          .catch(err => console.log(err));
      })
      .catch(err => console.log("decisions error ", err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: getClientServiceData,
    applySnapshot: (snap) => {
      if (snap.clientService) setClientService(snap.clientService);
      if (snap.clientService?.fulfillment) setFulfillment(snap.clientService.fulfillment);
    },
    deps: [year, districtId, getClientServiceData],
  });

  return (
    <Comment
      data={displayClientService}
      year={year}
      districtId={districtId}
      tableCommentedId={`c4.0-4.1-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={fulfillment} />
          </Col>

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Establishment of Client Service Unit</Title>
          <Table
            columns={clienServiceColumn}
            dataSource={transformDpatTableRows(displayClientService?.data || [], SERVICE_LINK_KEYS)}
            pagination={false}
            bordered
          />
          <br />
          {displayClientService && (
            <Table
              columns={clienServiceReportColumn}
              dataSource={transformDpatTableRows(displayClientService?.report, REPORT_LINK_KEYS)}
              pagination={false}
              bordered
            />
          )}
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default ClientServiceFunctionality;
